/**
 * SUPPLY-CHAIN DISRUPTION — the lookup layer over the sourced holiday data.
 *
 * Pure functions over `src/data/supplyChainHolidays.js`. No fetching, no state,
 * no writes. The data file owns the facts and their provenance; this file only
 * answers "what is disrupted on this day" and "does this record collide".
 *
 * 🔴 `dayKey` IS IMPORTED FROM lib/championships.js. THERE IS NO COPY HERE.
 *
 * History worth keeping, because it explains the shape of this file: when this
 * module was first written the tree had THREE implementations of `dayKey`
 * (CountdownCalendar.jsx, features/reviews/dispatch.js:280,
 * reviews/reviewCoverage.js:302) and the UTC fault had been independently fixed
 * in two of them. The consolidation into lib/championships.js was then still on
 * an unmerged branch, so this module took the caller's `dayKey` by INJECTION
 * rather than import a function that did not yet exist on main.
 *
 * That consolidation has since landed (championships.js:276), so the injection
 * is gone and this file imports the one real implementation. The reviews pair
 * is still deliberately left alone — dispatch.js also handles a date-only
 * STRING, which is different semantics rather than a duplicate.
 *
 * A FOURTH copy here was never an option and still is not.
 *
 * Range tests remain plain `YYYY-MM-DD` STRING comparisons, which are
 * chronologically ordered, so there is no timezone to get wrong at a window
 * edge. The month walker builds keys by zero-padding integers it already holds
 * — no Date involved, so the UTC hazard cannot arise. `parseLocalDay` likewise
 * builds from local parts rather than `new Date('2026-02-15')`, which parses as
 * UTC and lands on Feb 14 west of Greenwich.
 */
import { dayKey } from './championships.js'
import {
  HOLIDAYS, LANES, SEVERITY, SOURCES, CN_MAKEUP_WORKDAYS, KNOWN_GAPS,
} from '../data/supplyChainHolidays.js'

export { LANES, SEVERITY, SOURCES, KNOWN_GAPS }

/** `YYYY-MM-DD` → local midnight Date. Null for anything unparseable, so a
 *  malformed entry renders as unknown rather than as 1970 or as a crash. */
export function parseLocalDay(iso) {
  if (typeof iso !== 'string') return null
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!m) return null
  const [, y, mo, d] = m
  const dt = new Date(Number(y), Number(mo) - 1, Number(d))
  // Reject 2026-02-31 style rollovers rather than silently accepting March 3.
  if (dt.getMonth() !== Number(mo) - 1 || dt.getDate() !== Number(d)) return null
  return dt
}

/** Zero-padded `YYYY-MM-DD` from integer parts. No Date, so no UTC hazard, and
 *  not a reimplementation of anybody's `dayKey`. */
const keyOf = (y, m1, d) => `${y}-${String(m1).padStart(2, '0')}-${String(d).padStart(2, '0')}`

/**
 * Inclusive day-range test.
 *
 * `YYYY-MM-DD` is lexicographically ordered the same as chronologically, so
 * this is a plain string comparison — no Date, no timezone, nothing to get
 * wrong at a window edge. Both bounds are validated first so a malformed entry
 * fails closed (no match) rather than matching everything.
 */
function coversDay(startIso, endIso, key) {
  if (!startIso || !key) return false
  if (!parseLocalDay(startIso)) return false
  const end = endIso || startIso
  if (!parseLocalDay(end)) return false
  return key >= startIso && key <= end
}

/**
 * Does this entry have a usable effective window?
 *
 * ⚠️ UNKNOWN IS NOT FALSY-AND-FORGOTTEN. An entry whose window is UNKNOWN still
 * matters — it is an unquantified risk, not a safe day — so it is reported
 * against its OFFICIAL dates and carries `windowKnown: false` so the UI can say
 * "window unknown" instead of implying the official dates are the whole story.
 */
function windowIsUsable(h) {
  return Boolean(h.window && h.window.status !== 'UNKNOWN' && h.window.start)
}

/**
 * Every disruption touching a given day.
 *
 * `reach` says WHY the day matched, which is the distinction the whole feature
 * rests on:
 *   'official'  the day is inside the declared holiday
 *   'window'    the day is outside the holiday but inside the effective window
 *               (the shoulder — the part a statutory calendar would miss)
 *   'both'      inside both
 */
export function disruptionsOn(key, { year } = {}) {
  if (typeof key !== 'string' || !key) return []
  const out = []
  for (const h of HOLIDAYS) {
    if (year && h.year !== year) continue
    const inOfficial = coversDay(h.official?.start, h.official?.end, key)
    const inWindow = windowIsUsable(h) && coversDay(h.window.start, h.window.end, key)
    if (!inOfficial && !inWindow) continue
    out.push({
      ...h,
      reach: inOfficial && inWindow ? 'both' : inOfficial ? 'official' : 'window',
      windowKnown: windowIsUsable(h),
      lane: h.lane,
      laneMeta: LANES[h.lane],
      severityMeta: SEVERITY[h.severity] || SEVERITY.UNKNOWN,
    })
  }
  // Worst severity first so a day's headline is its worst problem, then by lane
  // for a stable order on screen.
  return out.sort((a, b) => {
    const d = (b.severityMeta.rank || 0) - (a.severityMeta.rank || 0)
    return d || String(a.lane).localeCompare(String(b.lane))
  })
}

/** Is this a Chinese make-up working day — a weekend JD actually works? */
export function makeupWorkdayOn(key) {
  if (typeof key !== 'string') return null
  return CN_MAKEUP_WORKDAYS.find((w) => w.date === key) || null
}

/** The distinct lanes disrupted on a day, in a fixed order. */
export function lanesOn(key) {
  const seen = new Set(disruptionsOn(key).map((d) => d.lane))
  return ['JD', 'SRE', 'US'].filter((l) => seen.has(l))
}

/** The worst severity on a day, for the cell's headline tone. */
export function worstSeverityOn(key) {
  const list = disruptionsOn(key)
  return list.length ? list[0].severityMeta : null
}

/**
 * Month rollup — every disrupted day in a month, keyed by day-of-month.
 *
 * Built once per month in the component rather than per cell, because the data
 * set is small but the grid asks 42 times.
 */
export function disruptionsForMonth(year, monthIndex) {
  const days = new Date(year, monthIndex + 1, 0).getDate()
  const byDay = {}
  for (let d = 1; d <= days; d++) {
    const key = keyOf(year, monthIndex + 1, d)
    const list = disruptionsOn(key)
    const makeup = makeupWorkdayOn(key)
    if (list.length || makeup) byDay[d] = { key, list, makeup }
  }
  return byDay
}

/**
 * COLLISION — a championship dated inside a disruption window.
 *
 * 🔴 READ THE NAME CAREFULLY. This is an EVENT-DATE collision, not a delivery
 * collision, and the difference is the difference between a real signal and a
 * fabricated one.
 *
 * SR does not currently hold a delivery or in-hands date on this surface:
 * championships are keyed on `effDate` (the event), while production lives on
 * the ClickUp journey order (list 901000306889, 14 stages off `mfg_release` in
 * lib/journey.js) with no join to a championship record here — and `In Hands
 * Date` on /api/clickup/tasks is still an open ask on BUILD LOG 868k89xtr.
 *
 * So this function does NOT add a lead time to an event date to synthesise a
 * delivery window. That would look like the asked-for feature and would be
 * invented: `mfg_release` is not the event date and nothing maps one to the
 * other. It reports only what is true — that the event itself lands in a window
 * — and the UI labels it as exactly that.
 */
export function eventDateCollisions(items, { year } = {}) {
  const out = []
  for (const c of items || []) {
    if (!c?.effDate) continue
    const key = dayKey(c.effDate)
    if (!key) continue
    const hits = disruptionsOn(key, { year })
    if (!hits.length) continue
    out.push({ record: c, day: key, disruptions: hits, worst: hits[0] })
  }
  return out.sort((a, b) => a.day.localeCompare(b.day))
}

/** Collision count by lane — for the Command Center summary line. */
export function collisionSummary(items) {
  const collisions = eventDateCollisions(items)
  const byLane = { JD: 0, SRE: 0, US: 0 }
  let worstRank = 0
  let worst = null
  for (const col of collisions) {
    const lanes = new Set(col.disruptions.map((d) => d.lane))
    for (const l of lanes) if (byLane[l] != null) byLane[l] += 1
    const r = col.worst?.severityMeta?.rank || 0
    if (r > worstRank) { worstRank = r; worst = col }
  }
  return { total: collisions.length, byLane, worst, collisions }
}

/** Resolve a source key to its record, for rendering provenance. */
export const sourceFor = (key) => SOURCES[key] || null

/**
 * Human sentence for an entry's official dates, including the UNKNOWN case.
 * Never returns an empty string and never returns a bare date with no year —
 * every one of these holidays moves, so a year-less date would be a trap.
 */
export function officialLabel(h) {
  const o = h?.official
  if (!o || o.status === 'UNKNOWN' || !o.start) return 'Official dates UNKNOWN'
  const s = parseLocalDay(o.start)
  const e = parseLocalDay(o.end || o.start)
  if (!s) return 'Official dates UNKNOWN'
  const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  const range = (o.end || o.start) !== o.start && e ? `${fmt(s)} – ${fmt(e)}` : fmt(s)
  return o.status === 'OBSERVED' ? `${range} (observed)`
    : o.status === 'ESTIMATE' ? `${range} (estimated, ±1 day)`
      : o.status === 'FORECAST' ? `${range} (forecast, unofficial)`
        : o.status === 'DISPUTED' ? `${range} (legal status disputed)`
          : range
}

/** Human sentence for the effective window, including the UNKNOWN case. */
export function windowLabel(h) {
  const w = h?.window
  if (!w || w.status === 'UNKNOWN' || !w.start) {
    return { text: 'Effective window UNKNOWN', known: false, reason: w?.reason || 'Not established by any source found.' }
  }
  const s = parseLocalDay(w.start)
  const e = parseLocalDay(w.end || w.start)
  if (!s) return { text: 'Effective window UNKNOWN', known: false, reason: 'Unparseable window in the data file.' }
  const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  const range = (w.end || w.start) !== w.start && e ? `${fmt(s)} – ${fmt(e)}` : fmt(s)
  return {
    text: w.status === 'FORECAST' ? `${range} (forecast)` : range,
    known: true,
    forecast: w.status === 'FORECAST',
    reason: w.reason || null,
  }
}

/** Day count of an effective window, or null when unknown. */
export function windowDays(h) {
  if (!windowIsUsable(h)) return null
  const s = parseLocalDay(h.window.start)
  const e = parseLocalDay(h.window.end || h.window.start)
  if (!s || !e) return null
  return Math.round((e - s) / 86400000) + 1
}

/** Day count of the official holiday, or null when unknown. */
export function officialDays(h) {
  const o = h?.official
  if (!o || o.status === 'UNKNOWN' || !o.start) return null
  const s = parseLocalDay(o.start)
  const e = parseLocalDay(o.end || o.start)
  if (!s || !e) return null
  return Math.round((e - s) / 86400000) + 1
}
